import { Box, Grid, PaginationItem, Typography } from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  gridPageCountSelector,
  GridPagination,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import PaginationNextButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationNextButtonIcon';
import PaginationPreviousButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationPreviousButtonIcon';
import SortIcon from '../../../../packages/common/shared-ui/src/icons/SortIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

// Original class: DataGridTableRoot
const StyledDataGridTableRoot = styled(DataGridPro)(({ theme }) => ({
  width: '100%',
  overflow: 'hidden',
  '&.MuiDataGrid-root': {
    border: 0,
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '1px solid #E4E4EF',
      '& .MuiBadge-badge': {
        display: 'none',
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
        '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            borderLeft: '1px solid #E4E4EF',
            '&.actions': {
              borderLeft: 'none',
            },
          },
        },
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeaderCheckbox':
        {
          minWidth: '36px!important',
          maxWidth: '36px!important',
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            paddingLeft: 0,
          },
        },
    },
    '& .MuiDataGrid-virtualScroller': {
      '&::-webkit-scrollbar': {
        height: '6px',
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
    '& .MuiDataGrid-row': {
      width: '100%',
      borderBottom: '1px solid #E4E4EF',
      '& .MuiDataGrid-rowReorderCellContainer': {
        borderRight: '1px solid #E4E4EF',
      },
      '&.errorRow': {
        outline: '1px solid #E03737',
      },
      '& .MuiDataGrid-cell': {
        '&.tableCell': {
          overflow: 'visible',
          '&.parent': {
            paddingLeft: 0,
          },
          '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
            '& + .MuiDataGrid-cell.tableCell': {
              borderLeft: '1px solid #E4E4EF',
            },
          },
          '&.tableGroupCell': {
            overflow: 'visible',
            whiteSpace: 'nowrap',
            '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
              '& + .MuiDataGrid-cell.tableCell': {
                borderLeft: '1px solid transparent',
              },
            },
          },
          '&.tableGroupGanttCell': {
            padding: '10px 10px 10px 0',
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox': {
        minWidth: '36px!important',
        maxWidth: '36px!important',
        '& + .MuiDataGrid-cell': {
          paddingLeft: 0,
        },
      },
      '&.MuiDataGrid-row--editing': {
        boxShadow: 'none',
      },
    },
    '& .MuiDataGrid-row.groupRow': {
      '&.confirmationRow': {
        borderTop: '4px solid #E4E4EF',
        '&.first-row': {
          borderTop: 'none',
        },
      },
      '& .MuiDataGrid-cellCheckbox': {
        boxShadow: 'none',
        '& .MuiCheckbox-root': {
          visibility: 'hidden',
        },
      },
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
      borderRight: 0,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiCheckbox-root': {
      '& .MuiSvgIcon-root': {
        width: '12px',
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      '&.MuiDataGrid-cell--editing': {
        boxShadow: 'none',
        '&:focus-within': {
          outline: 'none',
        },
      },
      '&.tableCell': {
        wordBreak: 'break-word',
        borderColor: 'transparent',
        position: 'relative',
        padding: '10px',
        '&.tableSelectCell': {
          overflow: 'visible',
        },
        '& a': {
          position: 'relative',
        },
        '&:last-child': {
          boxShadow: 0,
        },
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.cellEdit': {
          padding: 0,
        },
        '&.selectCell': {
          padding: 0,
        },
      },
      '&.actions': {
        padding: 0,
        '& .MuiDataGrid-actionsCell': {
          height: '100%',
          width: '100%',
          gridGap: 0,
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          height: '100%',
          borderRadius: '0',
          padding: '0',
          flex: 1,
          '& .MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          '& + .MuiButtonBase-root.MuiIconButton-root': {
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    '& .MuiDataGrid-cellCheckbox': {
      borderColor: 'transparent',
    },
    '& .MuiDataGrid-main': {
      borderRadius: '16px',
      backgroundColor: '#fff',
    },
  },

  '& .MuiDataGrid-footerContainer': {
    marginTop: '20px',
    minHeight: 'auto',
    border: 'none',
    justifyContent: 'flex-start',
    '& .MuiToolbar-root.MuiTablePagination-toolbar': {
      minHeight: 'auto',
      padding: 0,
      '& .MuiPagination-root': {
        marginLeft: 0,
        '& li': {
          fontSize: '16px',
          lineHeight: '20px',
        },
        '& li + li': {
          marginLeft: '2px',
        },
        '& .MuiButtonBase-root.MuiPaginationItem-root': {
          color: '#41424E',
          fontSize: '16px',
          lineHeight: '20px',
          borderRadius: '4px',
          minWidth: '28px',
          height: '28px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: '#6270D0',
            color: '#FAFAFC',
            '&:hover': {
              color: '#FAFAFC',
              backgroundColor: '#6270D0',
            },
          },
          '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
            width: 'auto',
            height: 'auto',
          },
          '&:hover': {
            color: '#6270D0',
            backgroundColor: 'transparent',
          },
        },
        '& .MuiPaginationItem-ellipsis': {
          fontSize: '16px',
          lineHeight: '20px',
          color: '#41424E',
          padding: 0,
          margin: 0,
          minWidth: ' 28px',
        },
      },
    },
  },
}));

const ConfirmationsTable = ({
  paginationData,
  columnsData,
  rowsData,
  changePagination,
  changeSorting,
}) => {
  const getLocalString = useTranslation();
  const pageUseParams = useParams();
  const [gridRows, setGridRows] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);
  const [pagination, setPagination] = useState({ count: 0, page: 0 });
  const [sortModel, setSortModel] = useState([]);
  const handleGetRedirectUrl = useCallback(
    ({ id, columnType }) => {
      switch (columnType) {
        case 'document':
          return `${window.location.origin}/issues/${id}`;
        case 'project':
          return `${window.location.origin}/projects/${id}`;
        case 'confirmation':
          return pageUseParams.projectId
            ? `/projects/${pageUseParams.projectId}/confirmations/${id}`
            : `${window.location.origin}/confirmations/${id}`;
        default:
          return '';
      }
    },
    [pageUseParams.projectId],
  );

  const cellRenderer = useCallback(
    ({ props, column }) => {
      const linkFields = ['project', 'confirmation'];
      const multiLinkFields = ['document'];
      if (!props.value && props.row.groupName) {
        if (props.colDef.position === 0) {
          return (
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
              }}
            >
              {props.row.groupName}
            </Typography>
          );
        }
      } else {
        if (linkFields.includes(column.field)) {
          return (
            <Link
              to={handleGetRedirectUrl({
                id: props.row[column.fieldId + 'Id'],
                columnType: column.field,
              })}
              target={'_blank'}
            >
              {props.value}
            </Link>
          );
        }
        if (multiLinkFields.includes(column.field)) {
          return (
            <Grid
              container
              sx={{
                flexDirection: 'column',
              }}
            >
              {props.value?.map((item, index) => {
                return (
                  <Link
                    key={index}
                    to={handleGetRedirectUrl({
                      id: props.row[column.fieldId + 'Id'].find(
                        (item, itemIndex) => itemIndex === index,
                      ),
                      columnType: column.field,
                    })}
                    target={'_blank'}
                  >
                    {item}
                  </Link>
                );
              })}
            </Grid>
          );
        }
      }
    },

    [handleGetRedirectUrl],
  );

  useEffect(() => {
    if (paginationData) {
      setPagination(() => ({
        page: paginationData.offset / 100,
        count: Math.ceil(paginationData.total / paginationData.limit),
      }));
    }
  }, [paginationData]);

  useEffect(() => {
    if (rowsData) {
      const rows = rowsData.filter(item => item.rowType !== 'total');
      const data = rows.reduce((acc, row, index) => {
        const newCols = row.columns.reduce((columnAcc, item, i) => {
          return {
            ...columnAcc,
            [row.columns[i].fieldId]: row.columns[i].value,
            [row.columns[i].fieldId + 'Id']: row.columns[i].valueId,
          };
        }, {});
        delete row.columns;
        return [...acc, { ...newCols, ...row, id: row.rowId }];
      }, []);
      setGridRows(data);
    }
  }, [rowsData]);

  useEffect(() => {
    if (columnsData) {
      const smallFields = ['date', 'bool'];
      const mediumFields = ['project', 'unapprover'];
      const bigFields = ['confirmation'];
      const data = columnsData.map(column => {
        let columnWidth = 200;
        let columnFlex = 0;
        smallFields.includes(column.field) && (columnWidth = 100);
        mediumFields.includes(column.field) && (columnWidth = 220);
        bigFields.includes(column.field) && (columnWidth = 240);
        return {
          field: column.fieldId,
          headerName: column.fieldName,
          renderHeader: props => (
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
              }}
            >
              {props.colDef.headerName}
            </Typography>
          ),
          disableExport: false,
          sortable: column.canSort,
          flex: columnFlex,
          width: columnWidth,
          cellClassName: props =>
            props.row.rowType === 'group'
              ? 'tableCell tableGroupCell'
              : 'tableCell',
          renderCell: props => cellRenderer({ props, column }),
        };
      });
      setGridColumns(data);
    }
  }, [cellRenderer, columnsData]);

  function Pagination({ page, onPageChange, className }) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    return (
      <MuiPagination
        color='primary'
        className={className}
        count={pagination.count}
        siblingCount={1}
        boundaryCount={0}
        page={pagination.page + 1}
        renderItem={item => {
          if (item.type === 'next') {
            return (
              !item.disabled && (
                <Box>
                  {getLocalString('labels', 'next')}
                  <PaginationItem
                    {...item}
                    slots={{
                      next: PaginationNextButtonIcon,
                    }}
                  ></PaginationItem>
                </Box>
              )
            );
          } else if (item.type === 'previous') {
            return (
              !item.disabled && (
                <Box>
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: PaginationPreviousButtonIcon,
                    }}
                  ></PaginationItem>
                  {getLocalString('labels', 'previous')}
                </Box>
              )
            );
          } else {
            return <PaginationItem {...item}></PaginationItem>;
          }
        }}
        onChange={(event, newPage) => {
          changePagination(newPage - 1);
        }}
      />
    );
  }
  function CustomPagination(props) {
    return (
      <GridPagination
        {...props}
        labelDisplayedRows={() => ''}
        rowsPerPageOptions={[]}
        ActionsComponent={Pagination}
      />
    );
  }
  const handleSorting = useCallback(
    newSortModel => {
      const validSortModel = newSortModel.filter(
        item =>
          item &&
          typeof item.field === 'string' &&
          ['asc', 'desc'].includes(item.sort),
      );

      setSortModel(validSortModel);
      changeSorting(validSortModel);
    },
    [changeSorting],
  );
  return (
    <Grid
      container
      sx={{
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <StyledDataGridTableRoot
        localeText={{
          noRowsLabel: getLocalString('labels', 'noData'),
        }}
        pagination
        disableColumnFilter
        disableRowSelectionOnClick
        disableColumnMenu
        columns={gridColumns}
        rows={gridRows}
        getRowId={item => item.id}
        sortModel={Array.isArray(sortModel) ? sortModel : []}
        onSortModelChange={handleSorting}
        sortingMode='server'
        slotProps={{
          baseCheckbox: {
            icon: <CheckboxIcon width='12' height='12' viewBox='0 0 12 12' />,
            checkedIcon: (
              <CheckboxIconChecked width='12' height='12' viewBox='0 0 12 12' />
            ),
          },
        }}
        slots={{
          pagination: CustomPagination,
          columnUnsortedIcon: () => (
            <SortIcon width='20' height='20' viewBox='0 0 20 20' />
          ),
          columnSortedAscendingIcon: () => (
            <SortIcon
              direction='up'
              width='20'
              height='20'
              viewBox='0 0 20 20'
            />
          ),
          columnSortedDescendingIcon: () => (
            <SortIcon
              direction='down'
              width='20'
              height='20'
              viewBox='0 0 20 20'
            />
          ),
        }}
        getRowClassName={params => params.row.groupName && 'groupRow'}
        isRowSelectable={params => !params.row.groupName}
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiTablePagination-spacer': {
            display: 'none',
          },
        }}
      />
    </Grid>
  );
};

export default memo(ConfirmationsTable);
